<template>
    <div
        v-if="product"
        class="p-relative light-light-background max-height"
    >
        <image-product :image-link="product.imageLink" v-if="verticalVersion" class="contain-within-300" />

        <ion-grid
            class="purple-purple-color d-flex flex-d-c flex-grow-1 max-width"
            :class="{'max-height': !verticalVersion, 'mt-3': verticalVersion}"
        >
            <ion-row class="max-height mb-5">
                <ion-col v-if="!verticalVersion" class="center adapt-image">
                    <image-product :image-link="product.imageLink" />
                </ion-col>

                <ion-col class="center max-width">
                    <div class="max-width">
                        <div class="product-details-grid">
                            <div class="octo-gradient-purple-color fw-b fs-larger" style="grid-area: title;">
                                {{ product.name }}
                            </div>


                            <div class="fw-b mobile-smaller">{{ $t('label.category') }}:</div>

                            <div class="mobile-smaller">
                                {{
                                    (product.itemGroup2) ? `${product.itemGroup1} / ${product.itemGroup2}` : `${product.itemGroup1}`
                                }}
                            </div>

                            <template v-if="product.itemNumber">
                                <div class="fw-b mobile-smaller">{{ $t('label.articleNo') }}:</div>

                                <div class="mobile-smaller">
                                    {{ product.itemNumber }}
                                </div>
                            </template>

                            <template v-if="product.promotions?.length">
                                <div class="fw-b mobile-smaller">{{ $t('object.promotion', product.promotions.length) }}:</div>

                                <div>
                                    <div v-for="promotion in product.promotions" :key="promotion.id" class="mobile-smaller link" @click.stop="openProductDetails(promotion.id)">
                                        {{ promotion.title }}
                                    </div>
                                </div>
                            </template>

                            <template v-if="product.notInStock">
                                <div class="fw-b fs-smaller">{{ $t('label.availability') }}:</div>

                                <div class="fs-smaller">
                                    {{ product.availabilityInformation }}
                                    ({{ $t(`enumT.${HIGHLIGHT_MESSAGE.NOT_IN_STOCK}`) }})
                                </div>
                            </template>

                            <template v-if="product.packaging">
                                <div class="d-flex" style="grid-column: span 2">
                                    <div class="center-y my-3 flex-grow-1">
                                        <div
                                            class="highlight-box w-mc px-2 fs-smaller fw-600 light-light-color highlight-bg-coral card-border"
                                            style="padding-top: 2px; padding-bottom: 2px;"
                                        >
                                            {{ product.packaging }}
                                        </div>
                                    </div>
                                    <div v-if="product.unitPrice" class="fw-b fs-larger center-y">
                                        {{ convertNumberWithComma(product.unitPrice.toFixed(3)) }} €
                                    </div>
                                    <div v-else-if="product.price" class="fw-b fs-larger center-y">
                                        {{ convertNumberWithComma(product.price.toFixed(3)) }} €
                                    </div>
                                </div>
                            </template>

                            <template v-if="product.isSpecialOffer || product.notInStock || product.isPromotion">
                                <div style="grid-column: span 2">
                                    <div class="d-flex center-y" style="gap: 5px;">
                                        <highlight-box
                                            v-if="product.isSpecialOffer"
                                            :color="HIGHLIGHT_COLOR.PURPLE" class="px-2 fs-14 fw-400"
                                        >
                                            {{ $t(`enumT.${HIGHLIGHT_MESSAGE.REDUCED}`) }}
                                        </highlight-box>

                                        <highlight-box
                                            v-if="product.notInStock"
                                            :color="HIGHLIGHT_COLOR.ORANGE" class="px-2 fs-14 fw-400"
                                        >
                                            {{ $t(`enumT.${HIGHLIGHT_MESSAGE.NOT_IN_STOCK}`) }}
                                        </highlight-box>

                                        <highlight-box v-if="product.isPromotion" :color="HIGHLIGHT_COLOR.CORAL"
                                            class="px-2 fs-14 fw-400">
                                            {{ $t(`enumT.${HIGHLIGHT_MESSAGE.PROMOTION}`) }}
                                        </highlight-box>
                                    </div>
                                </div>
                            </template>

                            <template
                                v-if="!catalog && !(!catalog && !userStore.permission[PERMISSION_KEY.LIST_ITEM_UPDATE])"
                            >
                                <div class="center-y">
                                    <span class="fw-b">{{ $t('label.targetStock') }}:</span>
                                </div>

                                <div>
                                    <ion-input
                                        label=""
                                        class="grey4-grey4-background center purple-purple-color ion-text-center amount-input center-input no-spinner"
                                        style="border-radius: 8px; max-width: 80px;"
                                        @click="select"
                                        @ionInput="saveTargetInput"
                                        :disabled="readOnly"
                                        :value="targetStock"
                                        inputmode="numeric"
                                        :type="INPUT_TYPE.NUMBER"
                                        min="0"
                                        step="1"
                                        ref="amountInput"
                                    />
                                </div>
                            </template>

                            <template v-if="showTags && !Boolean(persistentStore.offlineMode)">
                                <div class="fw-b">
                                    {{ $t('label.tags') }}:
                                </div>

                                <div>
                                    <div class="d-flex tags-container">
                                        <category-pill
                                            v-if="requiresPermission(PERMISSION_KEY.LIST_UPDATE)"
                                            outline
                                            @click="emit('tags')"
                                            wrap
                                            class="long-pill"
                                        >
                                            <div class="center">
                                                {{ $t('label.editTags') }}
                                                <ion-icon class="ml-1" src="assets/icon/pencil.svg" />
                                            </div>
                                        </category-pill>

                                        <category-pill
                                            v-for="tag of tags" :key="tag.id" disable-ripple class="light-light-color"
                                        >
                                            {{ tag.name }}
                                        </category-pill>
                                    </div>
                                </div>
                            </template>
                        </div>

                        <ion-row class="mt-3" v-if="!readOnly">
                            <ion-col
                                size="3"
                                v-if="!catalog && userStore.permission[PERMISSION_KEY.LIST_ITEM_DELETE]"
                                :offset="(!catalog && !userStore.permission[PERMISSION_KEY.LIST_ITEM_UPDATE]) ? '4.5' : '0'"
                            >
                                <button-delete
                                    class="max-size ion-no-margin catalog-delete"
                                    :fill="BUTTON_FILL.OUTLINE"
                                    :color="ION_VARIANT.CORAL"
                                    @click="emit('delete')"
                                />
                            </ion-col>

                            <ion-col
                                :size="fullWidth ? '12' : '9'"
                                v-if="!(!catalog && !userStore.permission[PERMISSION_KEY.LIST_ITEM_UPDATE])"
                                class="input-amount-col"
                            >
                                <input-amount
                                    v-model="amountModel"
                                    @change="emit('amount', $event)"
                                    :amount-step="product.amountStep"
                                />
                            </ion-col>
                        </ion-row>

                        <ion-row v-if="!isMultiple && !readOnly">
                            <ion-col class="coral-coral-color fs-small center-x">
                                {{ $t('label.multipleAmount', { target: props.product.amountStep ?? 1 }) }}
                            </ion-col>
                        </ion-row>

                        <ion-row
                            v-if="!isMax && !readOnly"
                        >
                            <ion-col class="coral-coral-color fs-small center-x">
                                {{ $t('label.maxAmount', { target: props.product.amountMax }) }}
                            </ion-col>
                        </ion-row>

                        <ion-row
                            v-if="(props.catalog || props.buttons) && (userStore.permission[PERMISSION_KEY.CART_ADD] || userStore.permission[PERMISSION_KEY.LIST_ITEM_CREATE]) && !readOnly"
                            class="center-x"
                        >
                            <ion-col
                                size="6"
                                v-if="userStore.permission[PERMISSION_KEY.CART_ADD]"
                                :class="{'center-x': !userStore.permission[PERMISSION_KEY.LIST_ITEM_CREATE]}"
                            >
                                <button-base
                                    :color="ION_VARIANT.CORAL"
                                    class="fw-b wrap-button tt-none"
                                    :class="{'mr-2': userStore.permission[PERMISSION_KEY.LIST_ITEM_CREATE]}"
                                    @click="emit('addCart', { product: product.name, amount })"
                                    :disabled="!isMultiple || !isMax || Boolean(persistentStore.offlineMode) || !amount"
                                >
                                    {{ $t('modal.addCart') }}
                                </button-base>
                            </ion-col>

                            <ion-col
                                size="6"
                                v-if="userStore.permission[PERMISSION_KEY.LIST_ITEM_CREATE]"
                                :class="{'center-x': !userStore.permission[PERMISSION_KEY.CART_ADD],
                                        'ion-justify-content-end d-flex': userStore.permission[PERMISSION_KEY.CART_ADD]
                                }"
                            >
                                <button-base
                                    :color="ION_VARIANT.CORAL"
                                    :fill="BUTTON_FILL.OUTLINE"
                                    class="fw-b wrap-button tt-none"
                                    :class="{'ml-2': userStore.permission[PERMISSION_KEY.CART_ADD]}"
                                    @click="emit('addList', { product: product.name, amount })"
                                    :disabled="!isMultiple || !isMax || (Boolean(persistentStore.offlineMode) && !catalog)"
                                >
                                    {{ $t('modal.addList') }}
                                </button-base>
                            </ion-col>
                        </ion-row>

                        <ion-row v-else-if="!readOnly && catalog">
                            <ion-col>
                                <button-base
                                    :color="ION_VARIANT.CORAL"
                                    class="fw-b wrap-button tt-none max-width"
                                    @click="emit('addCart', { product: product.name, amount })"
                                    :disabled="!isMultiple || !isMax || Boolean(persistentStore.offlineMode) || !amount"
                                >
                                    {{ $t('modal.addCart') }}
                                </button-base>
                            </ion-col>
                        </ion-row>

                        <ion-row v-else-if="!catalog && !readOnly">
                            <ion-col class="center-x" v-if="privateSupplier">
                                <button-base
                                    :color="ION_VARIANT.CORAL"
                                    class="fw-b wrap-button tt-none max-width"
                                    @click="$emit('edit-item')"
                                >
                                    {{ $t('button.edit') }}
                                </button-base>
                            </ion-col>
                            <ion-col class="center-x">
                                <button-base
                                    :color="ION_VARIANT.CORAL"
                                    class="fw-b wrap-button tt-none max-width"
                                    @click="modalController.dismiss()"
                                >
                                    {{ $t('button.ok') }}
                                </button-base>
                            </ion-col>
                        </ion-row>
                    </div>
                </ion-col>
            </ion-row>
        </ion-grid>
    </div>
</template>

<script setup lang="ts">
import { IonCol, IonGrid, IonIcon, IonInput, IonRow, modalController } from "@ionic/vue";
import { computed, ref } from "vue";

import InputAmount from "@/components/input/InputAmount.vue";
import ButtonDelete from "@/components/button/ButtonDelete.vue";
import ButtonBase from "@/components/button/ButtonBase.vue";
import CategoryPill from "@/components/various/CategoryPill.vue";
import ImageProduct from "@/components/various/ImageProduct.vue";

import { useUserStore } from "@/stores/user";
import { usePersistentStore } from "@/stores/persistent";

import { convertNumberWithComma } from "@/functions/converter";
import { isApp } from "@/functions/enviroment";

import { BUTTON_FILL, INPUT_TYPE, ION_VARIANT } from "@/types/enum/ionicEnum";
import { HIGHLIGHT_COLOR, HIGHLIGHT_MESSAGE } from "@/types/enum/propEnum";
import { PERMISSION_KEY } from "@/types/user";
import { CatalogProductProps } from "@/types/props";
import { CatalogProductEmits } from "@/types/emits";
import HighlightBox from "@/components/card/HighlightBox.vue";
import { requiresPermission } from "@/functions/check";

const props = defineProps<CatalogProductProps>()
const emit = defineEmits<CatalogProductEmits>()

const userStore = useUserStore()
const persistentStore = usePersistentStore()

const amountModel = computed<number>({
    get: () => {
        return props.amount ?? 0
    },
    set: (value) => {
        emit('amount', value)
    }
})

const select = async (event: Event): Promise<void> => {
    const target = event.currentTarget as HTMLIonInputElement;

    const input = await target.getInputElement();
    input.focus();
    input.select();
};

/* code from Ihor */
// clear input of chars and floats
const amountInput = ref();

const saveTargetInput = (event: CustomEvent): void => {
	// emit('targetAmount', Number(event.detail.value))
    const convertedNumber = Number(String(event.detail.value).replace(/[^0-9]*/g, ''));
    emit('targetAmount', Number(convertedNumber))
    amountInput.value.$forceUpdate()
}
/* end of code from Ihor */

const isMax = computed<boolean>(() => {
    return props.product.amountMax ? amountModel.value <= props.product.amountMax : true
})

const isMultiple = computed<boolean>(() => {
    return (amountModel.value % (props.product.amountStep ?? 1)) === 0
})

const fullWidth = computed<boolean>(() => {
    return props.catalog || !userStore.permission[PERMISSION_KEY.LIST_ITEM_DELETE]
})

const verticalVersion = ref<boolean>(isApp() && !window.matchMedia("(orientation: landscape)").matches)
window.addEventListener('orientationchange', () => verticalVersion.value = isApp() && !window.matchMedia("(orientation: landscape)").matches)
window.addEventListener('resize', () => verticalVersion.value = isApp() && !window.matchMedia("(orientation: landscape)").matches)

import { useNotificationStore } from "@/stores/notification";
import DashboardPromotionProduct from "@/components/modal/modalControllerModals/DashboardPromotionProduct.vue";
import { log } from "node:console";
const notificationStore = useNotificationStore();

const openProductDetails = async (informationId: string): Promise<void> => {
    const promotionIndex = notificationStore.allPromotions.findIndex((promotion) => promotion.item.id === informationId)

    if (promotionIndex === -1) return

    notificationStore.currentInformationIndex = promotionIndex

    await (await modalController.create({
        component: DashboardPromotionProduct,
        cssClass: 'product-information-modal'
    })).present()
}
</script>

<style scoped>
.image-container {
    height: 300px;
    width: auto;
}

.amount-input {
    width: 12ch;
    border-radius: 16px;
    --border-radius: 16px;
    --placeholder-color: var(--ion-color-grey1);
    --highlight-color-focused: none;
    min-height: 38px;
}

.input-amount-col {
    min-width: 214px;
}

.product-image {
    height: 100%;
    width: auto;
}

.tags-container {
    gap: 10px;
    flex-wrap: wrap;
}

.catalog-delete {
    --border-radius: 16px;
    min-height: 87px;
}

.md .center-input {
    --padding-start: 0 !important;
}

.ios .center-input {
    --padding-start: 10px;
}

.product-details-grid {
    display: grid;
    padding-inline: 10px;
    grid-gap: 10px;
    grid-template-columns: min-content auto;
    grid-template-areas:
        "title title"
}

@media (max-width: 396px) {
    .catalog-delete {
        min-height: 77px;
    }

    .long-pill {
        max-width: 200px;
    }

    .mobile-smaller {
        font-size: smaller;
    }
}

.link {
    color: var(--ion-color-coral);
    /* text-decoration: underline; */
    cursor: pointer;
}
</style>

<style>
@media (max-width: 768px) or (max-height: 768px) {
    .adapt-image {
        flex: 0 0 calc(4 / 12 * 100%);
        width: calc(4 / 12 * 100%);
        max-width: calc(4 / 12 * 100%);
    }
}
</style>